import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonPrimitive, IconButton, Text } from '@figma/fpl-components';
import * as stylex from '@stylexjs/stylex';
import { Icon24CircleUpLarge, Icon16Close } from '@figma/fpl-icons';
import { borderRadius, color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import { UNIVERSAL_UPGRADE_BADGE_EVENT_NAMES } from '@/consts/analytics.consts';
import { FF_UNIVERSAL_UPGRADE_BUTTON_ENTERPRISE } from '@/consts/featureFlags';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { PricingPageEntrypoint } from '@/enums/pricing-page.enum';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { I18N_KEYS } from '@/language/keys';
import { addRumAction } from '@/providers/datadog';
import { CreditsContext } from '@/services/CreditsContext';
import { useUserStore } from '@/state/user.state';
import { SubscriptionType } from '@/types/shared';
import { hasKnownPersonalEmailDomain } from '@/utils/email-domain.utils';

type UpgradeBadgeVariant = 'universal' | 'enterprise';

const styles = stylex.create({
  badge: {
    position: 'relative',
    backgroundColor: color.bgSecondary,
    borderRadius: borderRadius.large,
    overflow: 'hidden',
    marginBottom: spacer.spacer1,
  },
  dismissButton: {
    position: 'absolute',
    top: spacer.spacer1,
    right: 6,
  },
  inset: { marginInline: spacer.spacer2 },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacer.spacer2,
    paddingTop: spacer.spacer25,
    paddingInline: spacer.spacer3,
  },
  copy: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer1,
    width: '100%',
  },
  text: { margin: 0 },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer2,
    marginTop: spacer.spacer2,
    paddingBottom: spacer.spacer25,
    paddingInline: spacer.spacer25,
  },
  viewPlansLink: {
    color: 'inherit',
    cursor: 'pointer',
    textDecoration: 'underline',
    borderRadius: borderRadius.small,
    outline: { default: 'none', ':focus-visible': `1px solid ${color.borderSelected}` },
  },
});

/** Renders the selected upgrade entrypoint after flag and plan eligibility have been established. */
const UpgradeBadgeContent = ({
  subscriptionType,
  variant,
  onDismiss,
}: {
  subscriptionType: SubscriptionType;
  variant: UpgradeBadgeVariant;
  onDismiss?: () => void;
}) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { openUpgradeModalFromEntrypoint } = useContext(CreditsContext);
  const isFplMigrationEnabled = useFplMigrationFlag();
  const isEnterprise = variant === 'enterprise';
  const entrypoint = isEnterprise
    ? PricingPageEntrypoint.EnterpriseUniversalUpgradeBadge
    : PricingPageEntrypoint.UniversalUpgradeBadge;
  const copy = isEnterprise
    ? I18N_KEYS.NAVIGATION_DRAWER.ENTERPRISE_UPGRADE_BADGE
    : I18N_KEYS.NAVIGATION_DRAWER.UNIVERSAL_UPGRADE_BADGE;

  const handleUpgradeClick = () => {
    track(
      UNIVERSAL_UPGRADE_BADGE_EVENT_NAMES.ENTRYPOINT_CLICKED,
      {
        context: 'Payments',
        destination: 'pricing_modal',
        entrypoint,
        source: 'dashboard_left_rail',
        subscriptionType,
      },
      TrackTypeEnum.BI,
    );
    openUpgradeModalFromEntrypoint({ entrypoint });
  };

  const handleContactSalesClick = () => {
    track(
      UNIVERSAL_UPGRADE_BADGE_EVENT_NAMES.ENTRYPOINT_CLICKED,
      {
        context: 'Payments',
        destination: 'enterprise_contact_form',
        entrypoint,
        source: 'dashboard_left_rail',
        subscriptionType,
      },
      TrackTypeEnum.BI,
    );
    window.open(EXTERNAL_LINKS.enterpriseContactForm, '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="universal-upgrade-badge" {...stylex.props(styles.badge, isFplMigrationEnabled && styles.inset)}>
      {isEnterprise && onDismiss && (
        <div {...stylex.props(styles.dismissButton)}>
          <IconButton aria-label={t(I18N_KEYS.NAVIGATION_DRAWER.ENTERPRISE_UPGRADE_BADGE.DISMISS)} onClick={onDismiss}>
            <Icon16Close aria-hidden />
          </IconButton>
        </div>
      )}
      <div {...stylex.props(styles.content)}>
        <Icon24CircleUpLarge aria-hidden />
        <div {...stylex.props(styles.copy)}>
          <Text.h2 variant="body-medium-strong" color="default" align="center" {...stylex.props(styles.text)}>
            {t(copy.TITLE)}
          </Text.h2>
          <Text.p variant="body-medium" color="secondary" align="center" {...stylex.props(styles.text)}>
            {t(copy.DESCRIPTION)}
          </Text.p>
        </div>
      </div>
      <div {...stylex.props(styles.actions)}>
        <Button
          variant="primary"
          width="fill"
          size="md"
          onClick={isEnterprise ? handleContactSalesClick : handleUpgradeClick}
        >
          {t(copy.CTA)}
        </Button>
        {isEnterprise && (
          <Text.p variant="body-medium" color="default" align="center" {...stylex.props(styles.text)}>
            <Trans
              i18nKey={I18N_KEYS.NAVIGATION_DRAWER.ENTERPRISE_UPGRADE_BADGE.VIEW_PLANS}
              components={{
                viewPlansLink: (
                  <ButtonPrimitive onClick={handleUpgradeClick} {...stylex.props(styles.viewPlansLink)}>
                    {null}
                  </ButtonPrimitive>
                ),
              }}
            />
          </Text.p>
        )}
      </div>
    </div>
  );
};

/** Mounted only for paid plans; Free users never read or apply the saved dismissal. */
const DismissibleEnterpriseBadge = ({
  userId,
  subscriptionType,
}: {
  userId: string;
  subscriptionType: SubscriptionType;
}) => {
  const storageKey = `enterprise-uub:v1:dismissed:${userId}`;
  const { track } = useAnalytics();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  const dismiss = () => {
    setDismissed(true);
    let persisted = false;
    try {
      localStorage.setItem(storageKey, 'true');
      persisted = true;
    } catch {
      // Storage can be blocked or full; still hide the badge for this mount.
    }
    addRumAction('enterprise_upgrade_badge.dismiss', { persisted, subscriptionType });
    track(
      UNIVERSAL_UPGRADE_BADGE_EVENT_NAMES.DISMISSED,
      {
        context: 'Payments',
        entrypoint: PricingPageEntrypoint.EnterpriseUniversalUpgradeBadge,
        source: 'dashboard_left_rail',
        subscriptionType,
      },
      TrackTypeEnum.BI,
    );
  };

  return dismissed ? null : (
    <UpgradeBadgeContent subscriptionType={subscriptionType} variant="enterprise" onDismiss={dismiss} />
  );
};

const EnterpriseUpgradeBadgeGate = ({
  showUniversalFallback,
  subscriptionType,
}: {
  showUniversalFallback: boolean;
  subscriptionType: SubscriptionType;
}) => {
  const userId = useUserStore((state) => state.user?.uid);
  const canDismiss = [SubscriptionType.Starter, SubscriptionType.Pro, SubscriptionType.Team].includes(subscriptionType);
  const { value: isEnterpriseUpgradeButtonEnabled } = useFeatureFlag(FF_UNIVERSAL_UPGRADE_BUTTON_ENTERPRISE);

  if (isEnterpriseUpgradeButtonEnabled) {
    if (canDismiss && userId) {
      return <DismissibleEnterpriseBadge key={userId} userId={userId} subscriptionType={subscriptionType} />;
    }
    return <UpgradeBadgeContent subscriptionType={subscriptionType} variant="enterprise" />;
  }
  return showUniversalFallback ? (
    <UpgradeBadgeContent subscriptionType={SubscriptionType.Free} variant="universal" />
  ) : null;
};

/** Shows eligible standalone plan admins a contextual upgrade entrypoint in the dashboard left rail. */
export const UniversalUpgradeBadge = () => {
  const { isAllowedForPermission, isSubscriptionTypeIn } = useSubscriptionPermissions();
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const user = useUserStore((state) => state.user);
  const email = user?.email ?? null;
  const subscriptionType = user?.activeWorkspace?.subscription?.type ?? SubscriptionType.Free;
  const canUpgradePlan = isAllowedForPermission('plan', 'action');
  const isFreePlan = isSubscriptionTypeIn([SubscriptionType.Free]);
  const isEnterprisePlan = isSubscriptionTypeIn([SubscriptionType.Enterprise]);
  const hasNonPersonalEmail = Boolean(email && !hasKnownPersonalEmailDomain(email));
  const showUniversalFallback = isFreePlan && canUpgradePlan;

  if (isFigmaEmbedMode || isEnterprisePlan || !canUpgradePlan) {
    return null;
  }

  if (hasNonPersonalEmail) {
    return (
      <EnterpriseUpgradeBadgeGate showUniversalFallback={showUniversalFallback} subscriptionType={subscriptionType} />
    );
  }
  return showUniversalFallback ? (
    <UpgradeBadgeContent subscriptionType={SubscriptionType.Free} variant="universal" />
  ) : null;
};
